/**
 * Chat de soporte de Vision (reemplaza al Messenger de Intercom), para los negocios que la sesión
 * marca con `support_provider: 'vision'`.
 *
 * - El SDK lo sirve Vision (`/relay-sdk.js`), así sale con cada versión de Vision sin tocar Flash.
 * - El navegador sólo guarda una etiqueta al azar por sesión de Ágora; Cyclone decide a quién
 *   representa el chat y emite el acceso (`me/support/vision/*`). Nada sale bajo god mode.
 * - Las pantallas siguen llamando a `window.Intercom(...)` (abrir, esconder el botón, moverlo):
 *   el stub de index.html le pasa esas llamadas a `visionSupportCommand`.
 * - El último proveedor queda en localStorage para que index.html no baje Intercom al recargar.
 */
type Messenger = {
  open: (message?: string) => void;
  hide: () => void;
  update: (options: { hidden?: boolean; bottom?: number }) => void;
  shutdown: () => Promise<void>;
};
type Request = <T>(path: string, data: object, timeoutMs: number) => Promise<T>;

declare global {
  interface Window {
    __agoraSupportProvider?: 'vision' | 'intercom' | null;
    __visionSupportCommand?: (action: string, value?: unknown) => void;
    __loadIntercom?: () => void;
    __agoraIntercomLauncherHidden?: boolean;
    __agoraIntercomLoaded?: () => void;
  }
}

export const VISION_ORIGIN =
  (import.meta.env?.VITE_VISION_ORIGIN as string | undefined) || 'https://vision.agora.red';
/**
 * Chat de Vision también sin sesión (la página de entrada y el alta), en vez del Intercom
 * anónimo. Apagado hasta el día del corte: mientras tanto todo sigue igual que con Intercom.
 */
export const VISION_VISITOR_SUPPORT = import.meta.env?.VITE_VISION_VISITOR_SUPPORT === 'true';
const PROVIDER_KEY = 'agora:support-provider';
const SESSION_PREFIX = 'vision-support-session:';
const VISITOR_KEY = 'agora:support-visitor';

let realIntercom: unknown;

export function rememberSupportProvider(provider: 'vision' | 'intercom') {
  const previous = window.__agoraSupportProvider;
  window.__agoraSupportProvider = provider;
  // Si Intercom ya estaba bajando cuando la sesión eligió Vision (primera visita en un navegador),
  // termina de cargar después y pisa el stub: index.html avisa y se apaga igual que si ya estaba.
  window.__agoraIntercomLoaded = () => {
    if (window.__agoraSupportProvider === 'vision') rememberSupportProvider('vision');
  };
  try {
    localStorage.setItem(PROVIDER_KEY, provider);
  } catch {
    // Sin storage, la próxima carga decide de nuevo con la sesión.
  }
  const current = window.Intercom as ((...args: unknown[]) => void) & { __agoraStub?: boolean };
  if (provider === 'vision' && typeof current === 'function' && !current.__agoraStub) {
    // La primera vez Intercom pudo haber bajado antes de saber: se apaga y las llamadas de
    // las pantallas pasan a Vision. Desde la próxima carga ya no baja.
    try {
      current('shutdown');
    } catch {
      // Igual se reemplaza.
    }
    realIntercom = current;
    const shim = Object.assign(
      (action: string, value?: unknown) => window.__visionSupportCommand?.(action, value),
      {
        __agoraStub: true
      }
    );
    window.Intercom = shim;
  } else if (provider === 'intercom' && previous === 'vision') {
    if (realIntercom) window.Intercom = realIntercom;
    window.__loadIntercom?.();
  }
}

/**
 * Al salir de la cuenta: la página de entrada y el alta vuelven a tener Intercom, y la próxima
 * carga no lo frena por una sesión que ya no está.
 */
export function forgetSupportProvider() {
  // Con el chat de visitantes prendido, lo que sigue es ese chat de Vision: Intercom no baja.
  if (window.__agoraSupportProvider === 'vision' && !VISION_VISITOR_SUPPORT)
    rememberSupportProvider('intercom');
  try {
    localStorage.removeItem(PROVIDER_KEY);
    // Las etiquetas de sesión también: borrarlas cierra el chat en las otras pestañas, y si la
    // sesión se perdió (401) Cyclone revoca lo que quedó cuando vence, a los 20 minutos.
    const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
    for (const k of keys) if (k?.startsWith(SESSION_PREFIX)) localStorage.removeItem(k);
  } catch {
    // Sin storage no quedó nada guardado.
  }
}

/** Lo que las pantallas le piden a `window.Intercom`, traducido al chat de Vision. */
export function visionCommand(messenger: Messenger, action: string, value?: unknown) {
  if (action === 'show') messenger.open();
  // Sin texto también abre una conversación nueva, como Intercom (no el inicio del chat).
  else if (action === 'showNewMessage') messenger.open(typeof value === 'string' ? value : '');
  else if (action === 'hide' || action === 'shutdown') messenger.hide();
  else if (action === 'boot') messenger.update({ hidden: window.__agoraIntercomLauncherHidden ?? false });
  else if (action === 'update' && value && typeof value === 'object') {
    const v = value as Record<string, unknown>;
    const update: { hidden?: boolean; bottom?: number } = {};
    if (typeof v.hide_default_launcher === 'boolean') update.hidden = v.hide_default_launcher;
    if (typeof v.vertical_padding === 'number') update.bottom = v.vertical_padding;
    if (Object.keys(update).length) messenger.update(update);
  }
}

/** La etiqueta al azar de esta sesión de Ágora, compartida entre pestañas. */
function sessionLabel(key: string) {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
    const fresh = crypto.randomUUID();
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Monta el chat para `identity` (cuenta + persona de la sesión). Devuelve cómo cerrarlo al
 * salir de la cuenta: saca el chat en el momento y le pide a Cyclone que lo revoque.
 */
export function startVisionSupport(
  identity: string,
  request: Request,
  onUnavailable: () => void,
  loadSdk: () => Promise<{ createRelay: (options: object) => Messenger }> = () =>
    import(/* @vite-ignore */ `${VISION_ORIGIN}/relay-sdk.js`),
  // Un visitante no ve las Series: el chat pide acceso recién cuando lo abre, como Intercom con
  // un visitante (no se crea nadie en Vision por sólo pasar por la página).
  { proactivePosts = true }: { proactivePosts?: boolean } = {}
) {
  const key = SESSION_PREFIX + identity;
  const id = sessionLabel(key);
  let stopped = false;
  let messenger: Messenger | undefined;
  const queued: [string, unknown][] = [];
  // Lo último que pidieron las pantallas (botón escondido, altura), para reaplicarlo al remontar.
  const layout: { hidden?: boolean; bottom?: number } = {};
  const command = (action: string, value?: unknown) => {
    if (stopped) return;
    if (action === 'update' && value && typeof value === 'object') {
      const v = value as Record<string, unknown>;
      if (typeof v.hide_default_launcher === 'boolean') layout.hidden = v.hide_default_launcher;
      if (typeof v.vertical_padding === 'number') layout.bottom = v.vertical_padding;
    }
    if (messenger) visionCommand(messenger, action, value);
    else queued.push([action, value]);
  };
  window.__visionSupportCommand = command;
  // Lo que las pantallas pidieron antes de que este chat existiera quedó en la cola del stub de
  // index.html: pasa a Vision en vez de quedar esperando a un Intercom que no va a bajar.
  const stub = window.Intercom as { __agoraStub?: boolean; q?: ArrayLike<unknown>[] } | undefined;
  if (stub?.__agoraStub && Array.isArray(stub.q))
    for (const args of stub.q.splice(0)) command(String(args[0]), args[1]);
  const stop = () => {
    stopped = true;
    // Sólo si sigue siendo el de este chat: uno nuevo ya pudo haberlo reemplazado.
    if (window.__visionSupportCommand === command) window.__visionSupportCommand = undefined;
    // shutdown saca el iframe en el momento, antes de esperar la revocación.
    return messenger?.shutdown().catch(() => undefined) ?? Promise.resolve();
  };
  const onStorage = (e: StorageEvent) => {
    // Otra pestaña cerró la sesión de Ágora (borró la etiqueta): este chat se va también. Si dos
    // pestañas la crearon a la vez y quedó la de la otra, cada una sigue con la suya.
    if (e.key === key && e.newValue === null) void stop();
  };
  window.addEventListener('storage', onStorage);

  let remounts = 0;
  // Tope por página además del de seguidos: un servidor que alternara acceso y rechazo no gira
  // para siempre.
  let totalRemounts = 0;
  const mount = (createRelay: (options: object) => Messenger) => {
    // Una instancia que ya tuvo acceso y después es rechazada venció (p. ej. la compu durmió más
    // de 20 minutos con Ágora abierta y Cyclone la revocó): el chat se vuelve a montar con otra.
    let granted = false;
    let replaced = false;
    const current: Messenger = createRelay({
      origin: VISION_ORIGIN,
      alignment: 'right',
      // Los posts de las Series (p. ej. «tu página está por pausarse») aparecen sin abrir el chat.
      proactivePosts,
      getGrant: async (instance_id: string) => {
        if (stopped) throw new Error('Support session closed');
        try {
          const grant = await request<{ token: string }>('grant', { session_id: id, instance_id }, 8000);
          granted = true;
          remounts = 0;
          return grant;
        } catch (error) {
          // useClient sólo pone `statusCode` en los 404; Cyclone dice el motivo en `code`.
          const { statusCode, code } = (error ?? {}) as { statusCode?: number; code?: string };
          // Fuera de la cohorte (la sacaron hace un rato): vuelve a Intercom.
          if (statusCode === 404) {
            void stop();
            onUnavailable();
          } else if (
            code === 'instance_closed' &&
            granted &&
            !replaced &&
            remounts < 3 &&
            totalRemounts < 20
          ) {
            replaced = true;
            remounts++;
            totalRemounts++;
            setTimeout(() => {
              if (stopped || messenger !== current) return;
              void current.shutdown().catch(() => undefined);
              mount(createRelay);
            });
          }
          throw error;
        }
      },
      revoke: (instance_id: string) => request<void>('revoke', { session_id: id, instance_id }, 2500),
      navigate: (path: string) => window.location.assign(path)
    });
    messenger = current;
    current.update({ hidden: window.__agoraIntercomLauncherHidden ?? false, ...layout });
  };

  void loadSdk()
    .then(({ createRelay }) => {
      if (stopped) return;
      mount(createRelay);
      for (const [action, value] of queued.splice(0)) visionCommand(messenger!, action, value);
    })
    .catch(() => {
      // Vision no responde: el soporte no puede desaparecer, queda Intercom.
      if (!stopped) {
        void stop();
        onUnavailable();
      }
    });

  return {
    stop: () => {
      window.removeEventListener('storage', onStorage);
      return stop();
    },
    close: async () => {
      window.removeEventListener('storage', onStorage);
      void stop();
      try {
        localStorage.removeItem(key);
      } catch {
        // Igual se revoca del lado de Cyclone.
      }
      await Promise.race([
        request('logout', { session_id: id }, 2500).catch(() => undefined),
        new Promise<void>((resolve) => setTimeout(resolve, 2500))
      ]);
    }
  };
}

/**
 * Los pedidos del chat de visitantes a Cyclone (`support/visitor/*`). Sin token: quién es el
 * visitante lo dice la cookie HttpOnly que pone Cyclone, que esta página nunca ve.
 */
export function visitorSupportRequest(
  apiUrl: string,
  send: (url: string, init: RequestInit) => Promise<Response> = (url, init) => fetch(url, init)
): Request {
  return async <T>(action: string, data: object, timeoutMs: number) => {
    const { instance_id } = data as { instance_id?: string };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      // Con el chat de visitantes apagado Cyclone no habilita CORS y el navegador no deja leer el
      // 404: para el chat es lo mismo, vuelve a Intercom. Igual si Vision rechaza este sitio (403).
      const unavailable = (code?: string) =>
        Object.assign(new Error('Support unavailable'), { statusCode: 404, code });
      const res = await send(`${apiUrl}/support/visitor/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(instance_id ? { instance_id } : {}),
        signal: controller.signal
      }).catch((error) => {
        throw controller.signal.aborted ? error : unavailable('unreachable');
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 403 || res.status === 404) throw unavailable(body?.code);
      if (!res.ok)
        throw Object.assign(new Error('Support unavailable'), { statusCode: res.status, code: body?.code });
      try {
        // Para cerrar la visita al iniciar sesión, aunque el login sea en otra carga de la página.
        if (action === 'grant') localStorage.setItem(VISITOR_KEY, '1');
        if (action === 'logout') localStorage.removeItem(VISITOR_KEY);
      } catch {
        // Sin storage, la visita vence sola a los 30 días.
      }
      return body as T;
    } finally {
      clearTimeout(timer);
    }
  };
}

/**
 * Al iniciar sesión la visita se cierra, como pide Vision: sus conversaciones quedan para
 * soporte pero no pasan a la cuenta. Sólo si este navegador llegó a abrir el chat de visitante.
 */
export async function endVisitorSupport(request: Request) {
  try {
    if (!localStorage.getItem(VISITOR_KEY)) return;
  } catch {
    return;
  }
  await request('logout', {}, 2500).catch(() => undefined);
}
